/**
 * packages/ui/meterBlock.mjs — ONE PLACE THAT CAN TELL "YOU MAY NOT" FROM "IT BROKE".
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴🔴 WHY THIS EXISTS. RUN GC1 measured all four metered surfaces against the refusal bodies the
 * real meter produces. The wire was never the problem — every gate already answers with a
 * structured, machine-readable body. THE CLIENTS READ THE WRONG FIELD, OR NO FIELD AT ALL:
 *
 *   Doc Check   429 {error:'limit_reached', message:'Doc Checks aren't included on Free…'}
 *               → `setErrorMsg(data.error)` painted the literal string `limit_reached` in red.
 *                 (And on a real outage it painted `engine_unavailable`, so the defect was never
 *                  about quota — the surface renders WHATEVER machine code the server sends.)
 *
 *   Clark       429 {enabled:true, capped:true, error:'limit_reached', message:'…'}
 *               → the client tests `data.capped` (true) and then renders `data.reply` — a key the
 *                 DAILY-CAP body has and the PLAN-QUOTA body does not. `text: undefined` produced
 *                 an assistant row with the orb, the name "Clark", a timestamp and an empty bubble:
 *                 measured, ops/run-gc1/out — the founder's "answer that is never coming".
 *
 *   Research    HTTP 200 + `event: error` frame carrying the same body
 *               → `classifyFailure({status: payload?.status, reason: payload?.reason})`. The frame
 *                 carries NEITHER key, so `status == null` fell to RETRIEVAL_UNREACHABLE and the
 *                 product told the customer "I couldn't reach the research engine". THE ENGINE WAS
 *                 FINE. A 403 that reads as a 503 converts a sale into a support ticket and an
 *                 outage into a shrug.
 *
 * ⭐ THE COMMON SHAPE IS THIS REPO'S SIGNATURE HAZARD (`absence-of-an-answer-is-not-an-answer`):
 * a refusal collapsing into a CALM default because the field the reader branches on is absent from
 * the shape that actually arrived. So this module branches ONLY on fields the server demonstrably
 * sends, and returns `null` — never a guess — for everything else. A transport failure cannot come
 * out of here as a policy refusal, which is the property the whole run rests on.
 *
 * ⚠️ IT RECOGNISES, IT DOES NOT DECIDE. Entitlement is the server's. This reads an answer the
 * server already gave.
 */

import { PLAN_LIMITS, TIERS, TIER_LABEL, METER_LABEL, normalizePlan } from '@citeclerk/plan';
import { ACCOUNT_CODES } from './failure.mjs';

/** The four meters, in the order the product talks about them. */
export const METERS = ['queries', 'citations', 'docChecks', 'drafts'];

/**
 * Clerk feature key → the meter it gates. `featureBlock` (steel entitlement.js:181) names a
 * CAPABILITY, not a quota, and the two arrive as different bodies from different gates — but to the
 * customer they are one sentence ("this isn't on your plan"), so both resolve to a meter here.
 */
const FEATURE_TO_METER = {
  research: 'queries',
  doc_check: 'docChecks',
  matter_generation: 'drafts',
  citation_generation: 'citations',
};

/** Steel's search pools (`searchBlock`, entitlement.js:212) predate the meter names. */
const POOL_TO_METER = { account: 'queries', matter: 'queries' };

/**
 * The machine codes that mean POLICY. Every one of these was read off the route that emits it and
 * driven in a browser (ops/run-gc1/step0-*.mjs) — none is inferred from a name.
 */
const LIMIT_CODES = new Set(['limit_reached']);
const FEATURE_CODES = new Set(['feature_locked']);
const DAILY_CODES = new Set(['clark_daily_limit_reached', 'clark_anon_limit_reached', 'daily_limit_reached']);

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Read a response body and say whether it is a POLICY refusal.
 *
 * @param {any} body   the parsed JSON body, or an SSE `error` frame's payload
 * @returns {null | {
 *   kind: 'meter_block' | 'daily_cap',
 *   meter: string|null, feature: string|null, plan: string|null,
 *   limit: number|null, used: number|null, packs: number|null,
 *   serverMessage: string|null, upgradeUrl: string,
 * }}
 *
 * 🔴 `null` IS THE IMPORTANT RETURN VALUE. It is what keeps a dropped socket, a 500, a parse
 * failure and a genuine outage on the honest-failure path they already have. Widening the
 * recognisers below to "looks like a refusal" would re-create, in one module, the exact defect this
 * run removed from three.
 */
export function readMeterBlock(body) {
  if (!body || typeof body !== 'object') return null;

  // plan.js's citations shape carries the meter's own structured refusal inside it. Prefer the
  // inner one: it is the body every other meter sends, so one branch covers all four.
  if (body.meterBlock && typeof body.meterBlock === 'object') {
    const inner = readMeterBlock(body.meterBlock);
    if (inner) return inner;
  }

  const code = typeof body.error === 'string' ? body.error : null;

  if (code && DAILY_CODES.has(code)) {
    return {
      kind: 'daily_cap',
      meter: 'queries', feature: null,
      plan: body.tier ? normalizePlan(body.tier) : null,
      limit: num(body.limit), used: num(body.used), packs: null,
      // The daily-cap bodies say `reply`; the meter bodies say `message`. Both are read, and
      // NEITHER is invented — this is the one-key mismatch that emptied Clark's bubble.
      serverMessage: str(body.reply) || str(body.message),
      upgradeUrl: str(body.upgradeUrl) || '/pricing',
    };
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
     🔴🔴 A THIRD REFUSAL SHAPE, HANDED OVER BY RUN GX1 WHILE THIS RUN WAS OPEN.

     main's `featureGate` (apps/main/server/lib/plan.js:198) answers 403 with
     `{ error: "<a full sentence>", featureLocked: true, feature, plan, upgradeUrl }` — camelCase,
     the sentence in `error`, and NO machine code. Steel's `featureBlock` answers the same idea as
     `{ error: 'feature_locked', … }`. Two apps, one concept, two spellings; a recogniser that knew
     only the snake_case one would have left Magic Cite and Manual Cite rendering GX1's fallback
     sentence where a modal belongs.

     ⚠️ AND IT IS MARKED `capability: true`, WHICH THE MODAL BRANCHES ON. GX1's finding is that the
     production Clerk plans carry no features, so this gate currently refuses EVERY user on EVERY
     tier — including people who have paid for the thing. Deriving "upgrade to X" from a refusal
     that may be misfiring would tell a paying Attorney to buy a plan they already have. So a
     capability refusal quotes the SERVER'S OWN SENTENCE and only adds the derived upgrade line when
     PLAN_LIMITS agrees the plan genuinely has none of that meter. The gate is GX1's to fix; not
     compounding its error with a wrong upsell is mine. */
  if (body.featureLocked === true) {
    const feature = str(body.feature);
    const plan = body.plan ? normalizePlan(body.plan) : null;
    const m = FEATURE_TO_METER[feature] || null;
    // Only claim "not included" when the canonical table says so for THIS plan.
    const genuinelyZero = !!(plan && m && (PLAN_LIMITS[plan]?.[m] ?? 0) === 0);
    return {
      kind: 'meter_block', capability: true,
      meter: m, feature: feature || null, plan,
      limit: genuinelyZero ? 0 : null, used: null, packs: null,
      // This shape puts the SENTENCE in `error`; there is no separate `message`.
      serverMessage: str(body.error) || str(body.message),
      upgradeUrl: str(body.upgradeUrl) || '/pricing',
    };
  }

  if (code && FEATURE_CODES.has(code)) {
    const feature = str(body.feature);
    return {
      kind: 'meter_block',
      meter: FEATURE_TO_METER[feature] || null, feature: feature || null,
      plan: body.plan ? normalizePlan(body.plan) : null,
      limit: 0, used: null, packs: null,
      serverMessage: str(body.message),
      upgradeUrl: str(body.upgradeUrl) || '/pricing',
    };
  }

  if (code && LIMIT_CODES.has(code)) {
    const meter = str(body.meter) || POOL_TO_METER[str(body.pool)] || null;
    return {
      kind: 'meter_block',
      meter, feature: null,
      plan: body.plan ? normalizePlan(body.plan) : null,
      limit: num(body.limit), used: num(body.used), packs: num(body.packs),
      serverMessage: str(body.message),
      upgradeUrl: str(body.upgradeUrl) || '/pricing',
    };
  }

  // plan.js's `monthlyLimitReached` shape, when it arrived WITHOUT the nested meterBlock.
  if (body.monthlyLimitReached === true) {
    return {
      kind: 'meter_block',
      meter: 'citations', feature: null,
      plan: body.plan ? normalizePlan(body.plan) : null,
      limit: num(body.limit), used: null, packs: null,
      serverMessage: str(body.error) || null, // this shape puts the SENTENCE in `error`
      upgradeUrl: '/pricing',
    };
  }

  return null;
}

function str(v) { return typeof v === 'string' && v.trim() ? v.trim() : null; }

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴🔴 RUN HB — THE FOUR ACCOUNT REFUSALS, AND WHY `readMeterBlock` STILL RETURNS `null` FOR THEM.
 *
 * `out_of_searches`, `matter_cap_reached`, `authentication_required` and `payment_failed` are NOT
 * our engine failing, and RUN HB stopped all four from saying so (failure.mjs, `isAccount`). The
 * obvious next move is to widen `readMeterBlock` so they open the money modal too. THAT MOVE IS
 * WRONG, AND EACH ONE IS WRONG FOR ITS OWN MEASURED REASON:
 *
 *   out_of_searches         — the body carries NO `plan` (searchSpend.js:27). The modal derives its
 *                             pitch with `minimumTierFor(meter, plan || 'free')`, so with the plan
 *                             absent it would tell an Attorney Ultra customer — who has 50 queries
 *                             a month — that "Student Ultra includes 10 queries each month". That
 *                             is GC1's derived-tier rule failing in the direction it was written to
 *                             prevent: an upsell that is really a DOWNGRADE pitch.
 *   matter_cap_reached      — there is no `matters` meter in PLAN_LIMITS at all, so `meter` would be
 *                             null, and the modal's own title for a null meter is "That isn't
 *                             included on your plan" (MattersModals.jsx:180). The user is holding
 *                             999 matters. It is very much included. The title would be FALSE.
 *   authentication_required — the existing `auth_required` face promises "we'll email you a link to
 *                             finish". GB1 measured that path to be a `console.warn` stub
 *                             (billing.js:29). Routing here would repeat a sentence that is false.
 *   payment_failed          — a declined card is not a quota and has no meter, no limit and no used.
 *                             Everything the meter modal renders would have to be invented.
 *
 * ⭐ SO THE `null` IS THE ANSWER, NOT AN OMISSION — the same property GC1 rested this module on,
 * doing its job one run later. What is added instead is a SIBLING reader that describes an account
 * refusal honestly, including whether anything can truthfully be SOLD to fix it. Widening the modal
 * to these codes is real work with real copy, and it is handed back rather than faked here.
 *
 * @param {any} body  the parsed JSON body, or an SSE `error` frame's payload
 * @returns {null | { code, failureKind, meter, plan, limit, used, serverMessage, sellable, next }}
 *   `sellable` is FALSE unless PLAN_LIMITS independently names a tier with more of this meter for
 *   THIS plan. An unknown plan is never sellable — a guess about which tier to pitch is exactly the
 *   thing GC1's `minimumTierFor` exists to stop, and an absent `plan` field is not a free pass.
 */
export function accountRefusal(body) {
  if (!body || typeof body !== 'object') return null;
  const code = typeof body.error === 'string' ? body.error : null;
  const failure = code ? ACCOUNT_CODES[code] : null;
  if (!failure) return null;

  // `matter_cap_reached` is the pool that has no meter, and it must never be sellable: the server
  // itself sends `upgrade:false` because no tier lifts a flat per-account ceiling. An explicit
  // `upgrade:false` from ANY emitter is honoured — the server knows things this table does not.
  const meter = POOL_TO_METER[str(body.pool)] || (code === 'out_of_searches' ? 'queries' : null);
  const plan = body.plan ? normalizePlan(body.plan) : null;
  const next = (plan && meter && body.upgrade !== false) ? minimumTierFor(meter, plan) : null;

  return {
    code,
    failureKind: failure.kind,
    meter, plan,
    limit: num(body.limit), used: num(body.used),
    serverMessage: str(body.message) || str(body.reply),
    sellable: !!next,
    next,
  };
}

/**
 * THE MINIMUM TIER THAT ACTUALLY UNLOCKS THIS METER, DERIVED — never named.
 *
 * 🔴 RUN GC1's brief asserted "Free → queries is Attorney, not Student". The table disagrees:
 * `student_ultra` has `queries: 10`, so the minimum sufficient tier for a Free user is STUDENT
 * ULTRA. Naming a tier in copy is how an upsell sends a buyer to a plan that will refuse them
 * again — in either direction. So it is computed from PLAN_LIMITS every time.
 *
 * @returns {{ tier: string, label: string, includes: number } | null}
 *          null when no tier includes the meter (nothing true to say → say nothing).
 */
export function minimumTierFor(meter, fromPlan = 'free') {
  if (!meter || !METERS.includes(meter)) return null;
  const from = normalizePlan(fromPlan || 'free');
  const start = Math.max(0, TIERS.indexOf(from));
  const have = PLAN_LIMITS[from]?.[meter] ?? 0;
  for (let i = start; i < TIERS.length; i++) {
    const tier = TIERS[i];
    const includes = PLAN_LIMITS[tier]?.[meter] ?? 0;
    if (tier !== from && includes > have) return { tier, label: TIER_LABEL[tier] || tier, includes };
  }
  return null;
}

/** The customer-facing noun for a meter. `queries` covers Clark AND Research — "searches" does not. */
export function meterNoun(meter, n = 2) {
  if (!meter) return 'requests';
  const label = METER_LABEL[meter] || meter;
  if (n !== 1) return label;
  return { queries: 'query', citations: 'citation', docChecks: 'Doc Check', drafts: 'draft' }[meter] || label;
}

/**
 * Open the shared money modal for a policy refusal.
 *
 * @param {object} blockInfo  the output of `readMeterBlock`
 * @param {object} [extra]    `{ kept }` — a TRUE sentence about what the caller preserved. The
 *                            caller supplies it because only the caller knows: Draft's "Your
 *                            matters and sources are untouched" is the pattern, and a reassurance
 *                            that is not true on this surface must be omitted, not adapted.
 */
export function openMeterModal(blockInfo, extra = {}) {
  if (!blockInfo || typeof window === 'undefined') return false;
  window.dispatchEvent(new CustomEvent('cc:money-modal', { detail: { ...blockInfo, ...extra } }));
  return true;
}

export default { readMeterBlock, accountRefusal, minimumTierFor, meterNoun, openMeterModal, METERS };
